import "../../../sbscription/css/style.css";
import "./onboarding.scss";

import _ from "lodash";
import type * as Oidc from "oidc-client-ts";
import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";

import { LoadingOverlay } from "../../components/LoadingOverlay/LoadingOverlay";
import { useUser } from "../../contexts/userContext";
import { useCreateApplication } from "../../hooks/reactQueryHooks/useApplications";
import type {
  IGenerateKeyProps,
  IKey,
} from "../../hooks/reactQueryHooks/useKeys";
import { useGenerateKey } from "../../hooks/reactQueryHooks/useKeys";
import type {
  ISubscription,
  Organization,
} from "../../hooks/reactQueryHooks/useSubscriptions";
import {
  activateSubscription,
  useSubscriptions,
} from "../../hooks/reactQueryHooks/useSubscriptions";
import { userManager } from "../auth/components/AzureLoginHelper";
import { ActivateComponent } from "./ActivateComponent";
import { CongratulationComponent } from "./CongratulationComponent";
import { ErrorView } from "./ErrorView";

export interface IKeyData {
  name: string;
  key: string;
}

interface IFormData {
  name: string;
  email: string;
  tenant: string;
  subscriptionName: string;
  subscriptionId: string;
  offerId: string;
  planId: string;
  status: string;
  organizationId: string;
  organizations: Organization[];
}

const getOnboardingData = (user: any, data: ISubscription): IFormData => {
  const { firstName, lastName, email } = user;

  const {
    beneficiaryTenantId,
    externalName,
    externalId,
    planId,
    offerId,
    status,
    organizations,
    organizationId,
  } = data;

  return {
    name: `${firstName} ${lastName}`,
    email,
    tenant: beneficiaryTenantId,
    subscriptionName: externalName,
    subscriptionId: externalId,
    offerId,
    planId,
    status,
    organizations,
    organizationId,
  };
};

const OnboardingPage: React.FC = () => {
  const [loading, setLoading] = useState<boolean>(false);

  const { user: extUser, setUser: setExtUser } = useUser();

  const [activated, setActivated] = useState<boolean>(false);

  const [keyData, setKeyData] = useState<IKeyData | undefined>();

  const [userExist, setUserExist] = useState<boolean>(false);

  const [subscriptionId, setSubscriptionId] = useState<string>("");

  const [formData, setFormData] = useState<IFormData | undefined>(
    {} as IFormData,
  );
  const [selectedOrganization, setOrganization] = useState<string>("");

  const [planId, setPlanId] = useState<string>("");

  const [loadingStatus, setLoadingStatus] = useState<boolean>(false);

  const [hasError, setHasError] = useState<boolean>(false);

  const [errorMessage, setErrorMessage] = useState<string>(
    "This organization already has a subscription.",
  );

  const navigate = useNavigate();
  const generateKeyMutation = useGenerateKey();
  const createAppMutation = useCreateApplication();

  const [searchParams] = useSearchParams();

  const [user, setUser] = useState<Oidc.User | null>(null);

  const token: string | null = searchParams.get("token");
  const decodedToken: string | null = token ? decodeURIComponent(token) : null;
  const formDataExist = !_.isEmpty(formData);

  const {
    data: subscriptionData = {},
    isLoading,
    error,
    isError,
    isFetching,
    refetch,
  } = useSubscriptions(decodedToken);

  useEffect(() => {
    if (isError && error) {
      if (error.response?.status === 401) {
        setErrorMessage("401: Unauthorized");
        return;
      }
      if (error.response?.status === 400) {
        setErrorMessage(
          error.response?.data[0]?.code === "OrganizationNotFound"
            ? "400: No available organizations found to assign the subscription to."
            : "400: Organization has subscription",
        );
        return;
      }
      setErrorMessage(
        error.response?.data[0]?.code
          ? `${error.response?.status}: ${error.response?.data[0]?.code}`
          : "",
      );
    }
  }, [isError, error]);

  const handleActivateSubscription = async () => {
    const { id, planId, organizationId } = subscriptionData;
    setHasError(false);
    const orgId = organizationId || selectedOrganization;

    const firstKeyName = "CogCache API Key";
    setLoading(true);

    try {
      const activated: boolean = await activateSubscription(id, planId, orgId);
      if (activated) {
        setActivated(true);
        await userManager.signinSilent();

        const loggedUser = await userManager.getUser();

        if (loggedUser) {
          console.log("LOGGED USER", organizationId, loggedUser);
          setExtUser(loggedUser as any);
        }
        const application = await createAppMutation.mutateAsync({
          name: "CogCache Project",
          id: orgId,
        });

        const fetchedKeyData: IKey = await generateKeyMutation.mutateAsync({
          organizationId: orgId,
          name: firstKeyName,
          applicationId: application.id,
        });

        const generatedKey: string = fetchedKeyData.key || "";
        const generatedKeyName: string = fetchedKeyData.name;

        console.log("Fetched key data", fetchedKeyData);

        if (generatedKey && generatedKeyName) {
          setKeyData({
            key: generatedKey,
            name: generatedKeyName,
          } as IKeyData);
        }
      } else {
        setHasError(true);
      }

      setLoading(false);
    } catch (error) {
      setHasError(true);
      setLoading(false);
    }
  };

  async function getStarted() {
    setLoadingStatus(true);
    if (!!subscriptionId && !!planId) {
      setLoadingStatus(false);
      navigate("/safety-compliance/cache");
    } else {
      setLoadingStatus(false);
    }
  }

  const handleOrganizationChange = (
    event: React.ChangeEvent<HTMLSelectElement>,
  ) => {
    setOrganization(event.target.value);
  };

  useEffect(() => {
    if (extUser) {
      // eslint-disable-next-line @typescript-eslint/no-floating-promises
      refetch();
      setUser(extUser as any);
      setUserExist(true);
    }
  }, [extUser, refetch]);

  useEffect(() => {
    const subscgiptionExist = !_.isEmpty(subscriptionData);
    if (userExist && subscgiptionExist && !formDataExist) {
      const parcedFormData: IFormData = getOnboardingData(
        user,
        subscriptionData,
      );
      setFormData(parcedFormData);
      if (!subscriptionId && subscriptionData?.id) {
        setSubscriptionId(subscriptionData.id);
      }
      if (!planId && subscriptionData?.planId) {
        setPlanId(subscriptionData.planId);
      }
    }
  }, [
    userExist,
    subscriptionData,
    user,
    formDataExist,
    subscriptionId,
    planId,
  ]);

  if (isError) {
    return <ErrorView errorMessage={errorMessage} />;
  }

  return (
    <>
      <>
        {formDataExist && !activated && (
          <ActivateComponent
            name={formData?.name ?? ""}
            email={formData?.email ?? ""}
            tenant={formData?.tenant ?? ""}
            subscriptionName={formData?.subscriptionName ?? ""}
            subscriptionId={formData?.subscriptionId ?? ""}
            offerId={formData?.offerId ?? ""}
            planId={formData?.planId ?? ""}
            status={formData?.status ?? ""}
            organizationId={formData?.organizationId ?? ""}
            organizations={formData?.organizations ?? []}
            selectedOrganization={selectedOrganization ?? ""}
            isActive={false}
            isLoading={loading}
            onActivate={handleActivateSubscription}
            getStarted={getStarted}
            handleOrganizationChange={handleOrganizationChange}
            hasError={hasError}
          />
        )}
      </>
      <>
        {!isLoading && activated && !loading && (
          <CongratulationComponent
            keyName={keyData?.name}
            keyData={keyData?.key}
            loadingStatus={loadingStatus}
            userExist={userExist}
            getStarted={getStarted}
          />
        )}
      </>
      {(isLoading || isFetching) && <LoadingOverlay />}
    </>
  );
};

export { OnboardingPage };
